import { generateConfig } from "../utils";
import { API_URL_CONFIG_TYPE } from "../types";
import { getOverseasRegion } from "./domain";

import {
    MSCU, MSNA, TBPR, APAC, JPN_KOR, OCEANIA, EMEA, AM, FE_MSCU, FE_MSNA, FE_TBPR,
    BOT_GAUSIUM_CN, BOT_GAUSIUM_EU, BOT_GAUSIUM_US,
    ESCALADE_GAUSIUM_EU, ESCALADE_GAUSIUM_US,
    CCS_GAUSIUM_EU, CCS_GAUSIUM_US,
} from "./constants";
export { MSCU, MSNA, TBPR, APAC, JPN_KOR, OCEANIA, EMEA, AM, FE_MSCU, FE_MSNA, FE_TBPR } from "./constants";


export type RegionEndpoint = keyof typeof REGION_API_DOMAIN_MAP;

const { apiGetway: apiGetwayUrl, homeUrl, serviceUrl, escalade: escaladeUrl, mobileUrl, ccsUrl } = generateConfig() as API_URL_CONFIG_TYPE;
const region = process.env.REACT_APP_REGION;
const env = process.env.REACT_APP_NODE_ENV;
const isProdEnv = env === "prod";

export const HOME_CN_URL = isProdEnv ? "https://home.gs-robot.com" : homeUrl;

export const PLATFORM_REGION_MAP = {
    'us': MSNA,
    'eu': MSCU,
}

// header运维大区头
export const GS_USER_REGION_MAP = {
    [JPN_KOR]: JPN_KOR, // 东亚区
    [APAC]: APAC, // 亚太区
    [FE_TBPR]: "", // 东部中国区
    [FE_MSCU]: EMEA, // 欧洲区
    [FE_MSNA]: AM, // 美洲区
}

export const COLUMBUS_REGION_MAP = {
    [JPN_KOR]: FE_MSNA, // 东亚区
    [APAC]: FE_MSCU, // 亚太区
    [FE_TBPR]: FE_TBPR, // 东部中国区
    [FE_MSCU]: FE_MSCU, // 欧洲区
    [FE_MSNA]: FE_MSNA, // 美洲区
}

export const REGION_MAP = isProdEnv ? {
    [TBPR]: "默认区域",
    [MSCU]: "欧洲区",
    [MSNA]: "美洲区",
    [JPN_KOR]: "东亚区",
    [APAC]: "亚太区",
} : {
    [TBPR]: "默认区域",
}

export const FE_REGION_MAP = isProdEnv ? {
    [FE_TBPR]: "默认区域",
    [FE_MSCU]: "欧洲区",
    [FE_MSNA]: "美洲区",
    [JPN_KOR]: "东亚区",
    [APAC]: "亚太区",
} : {
    [FE_TBPR]: "默认区域",
}

export const REGION_SELECT_LIST = Object.entries(FE_REGION_MAP).map(([key, value]) => ({
    label: value,
    value: key,
}));

// ─── URL 配置类型 ─────────────────────────────────────────────────────────────

interface RegionNavUrls {
    home: string;
    service: string;
    mobile: string;
}

interface RegionApiUrls {
    /** bot API 域名（EndpointManager 路由入口） */
    api: string;
    /** escalade（SocketIO）域名 */
    escalade: string;
    /** CCS 域名 */
    ccs: string;
}

// ─── gs-robot.com 体系：按子区域解析 API 域名 ─────────────────────────────────
/**
 * 根据子区域代码生成 gs-robot.com 体系的 API 域名三元组。
 * api / escalade / ccs 三者均遵循统一命名规则：
 *   bot-{sub}.gs-robot.com / escafe-{sub}.gs-robot.com / ccsfe-{sub}.gs-robot.com
 */
const gsApiUrlsForSubregion = (sub: string): RegionApiUrls => ({
    api:      `bot-${sub}.gs-robot.com`,
    escalade: `escafe-${sub}.gs-robot.com`,
    ccs:      `ccsfe-${sub}.gs-robot.com`,
});

/**
 * 各 columbus 大区下属的子区域集合。
 * 只有当前站点子区域属于 columbus 同大区时，才走本地化 bot；否则走对方大区的默认 bot。
 */
const GS_COLUMBUS_SUBREGIONS: Record<string, Set<string>> = {
    [MSCU]: new Set(['eu', 'uk', 'ap']),
    [MSNA]: new Set(['us', 'ne']),
};

/**
 * 根据 columbus endpoint 返回 gs-robot.com 体系的 API 域名三元组。
 *
 * 路由规则（严格遵循 project.md 流程图）：
 * - MSCU → 当前站点属于 MSCU 子区域（eu/uk/ap）时走本地化 bot，否则走 bot-eu（MSCU 大区默认）
 * - MSNA → 当前站点属于 MSNA 子区域（us/ne）时走本地化 bot，否则走 bot-us（MSNA 大区默认）
 * - TBPR → 走 env lane 配置（国内 dev 环境）
 */
const getGsApiUrls = (columbus: string): RegionApiUrls => {
    // 各 columbus 大区的默认子区域（跨区时路由到此 bot）
    const defaultSubMap: Record<string, string> = { [MSCU]: 'eu', [MSNA]: 'us' };
    const defaultSub = defaultSubMap[columbus];
    if (defaultSub) {
        const currentSub = getOverseasRegion().toLowerCase();
        // 同大区：走本地化 bot；跨大区：走目标大区默认 bot
        const sub = GS_COLUMBUS_SUBREGIONS[columbus].has(currentSub) ? currentSub : defaultSub;
        return gsApiUrlsForSubregion(sub);
    }
    // TBPR：走 env lane 配置
    return { api: apiGetwayUrl, escalade: escaladeUrl, ccs: ccsUrl };
};

/**
 * gausium-robot.com 体系的固定 API 域名（与子区域无关）。
 */
const GAUSIUM_API_MAP: Record<string, RegionApiUrls> = {
    [MSCU]: { api: BOT_GAUSIUM_EU, escalade: ESCALADE_GAUSIUM_EU, ccs: CCS_GAUSIUM_EU },
    [MSNA]: { api: BOT_GAUSIUM_US, escalade: ESCALADE_GAUSIUM_US, ccs: CCS_GAUSIUM_US },
    [TBPR]: { api: BOT_GAUSIUM_CN, escalade: 'escafe.gs-robot.com', ccs: 'ccsfe.gs-robot.com' },
};

/**
 * 根据 columbus endpoint 返回当前部署体系（gausium 或 gs-robot.com）的 API 域名三元组。
 * `region` 非空时表示 gausium 部署环境。
 */
const getApiUrls = (columbus: string): RegionApiUrls =>
    region ? GAUSIUM_API_MAP[columbus] : getGsApiUrls(columbus);

// ─── 导航域名（home / service / mobile）──────────────────────────────────────
/**
 * gausium-robot.com 体系的导航域名（home/service/mobile 与 gs-robot.com 无关）。
 * api/escalade/ccs 已由 getApiUrls 统一处理，此处不再重复定义。
 */
const GAUSIUM_NAV_MAP: Record<string, RegionNavUrls> = {
    [MSCU]: { home: 'https://home.eu.gausium-robot.com', service: 'https://service.eu.gausium-robot.com', mobile: 'https://mobile.eu.gausium-robot.com' },
    [MSNA]: { home: 'https://home.us.gausium-robot.com', service: 'https://service.us.gausium-robot.com', mobile: 'https://mobile.us.gausium-robot.com' },
    [TBPR]: { home: 'https://home.gs-robot.com',         service: 'https://service.gs-robot.com',         mobile: 'https://mobile.gs-robot.com' },
};

/** gs-robot.com 体系的导航域名（MSCU/MSNA 共用 gausium 导航域名，TBPR 走 env lane）。 */
const GS_NAV_MAP: Record<string, RegionNavUrls> = {
    [MSCU]: GAUSIUM_NAV_MAP[MSCU],
    [MSNA]: GAUSIUM_NAV_MAP[MSNA],
    [TBPR]: { home: homeUrl, service: serviceUrl, mobile: mobileUrl },
};

const NAV_MAP = region ? GAUSIUM_NAV_MAP : GS_NAV_MAP;

// ─── 对外导出的各类域名 Map ───────────────────────────────────────────────────
export const REGION_HOME_URL_MAP = {
    [MSCU]: NAV_MAP[MSCU].home,
    [MSNA]: NAV_MAP[MSNA].home,
    [TBPR]: NAV_MAP[TBPR].home,
};

export const REGION_SERVICE_URL_MAP = {
    [MSCU]: NAV_MAP[MSCU].service,
    [MSNA]: NAV_MAP[MSNA].service,
    [TBPR]: NAV_MAP[TBPR].service,
};

export const REGION_DOMAIN_MAP = REGION_SERVICE_URL_MAP;

export const REGION_MOBILE_URL_MAP = {
    [MSCU]: NAV_MAP[MSCU].mobile,
    [MSNA]: NAV_MAP[MSNA].mobile,
    [TBPR]: NAV_MAP[TBPR].mobile,
};

// 国内站（gs-robot.com + TBPR）走 env lane；gausium 站（TBPR）走 bot.gausium-robot.com（仅生产）
export const REGION_API_DOMAIN_MAP = {
    [MSCU]: getApiUrls(MSCU).api,
    [MSNA]: getApiUrls(MSNA).api,
    [TBPR]: getApiUrls(TBPR).api,
};

// socketio 地址
export const ESCA_API_DOMAIN_MAP = {
    [MSCU]: getApiUrls(MSCU).escalade,
    [MSNA]: getApiUrls(MSNA).escalade,
    [TBPR]: getApiUrls(TBPR).escalade,
};

export const CCS_API_DOMAIN_MAP = {
    [MSCU]: getApiUrls(MSCU).ccs,
    [MSNA]: getApiUrls(MSNA).ccs,
    [TBPR]: getApiUrls(TBPR).ccs,
};

export const FE_DC_REGION_MAP = {
    "eclr": MSCU,  // e + "clr" (Europe → "clear" 晴朗，暗示欧洲气候)
    "ufog": MSNA,  // u + "fog" (US → "fog" 雾，暗示北美常见天气)
    "cmon": TBPR,  // c + "mon" (China → "monsoon" 季风，暗示亚洲气候)
}

// 后端的columbus映射前端endpoint的值
export const BE_DC_REGION_MAP = {
    [MSCU]: FE_MSCU,
    [MSNA]: FE_MSNA,
    [TBPR]: FE_TBPR,
}

// 根据后端返回的columbus，获取最终的区域endpoint
export const getRegionEndpoint = (columbus: string | undefined | null): RegionEndpoint => {
    if (columbus === TBPR || !columbus) {
        return TBPR;
    }
    return columbus as RegionEndpoint;
}

// 判断后端返回的columbus是否与当前前端选中区域的 endpoint 一致
export const isEquivalentEndpoint = (columbus: string, endpoint: RegionEndpoint): boolean => {
    return getRegionEndpoint(columbus) === endpoint;
}

// 兼容运维大区 和 columbus 的显示逻辑
export const showRegionArea = (endpoint: string, maintenanceRegionCode: string) => {
    // 欧洲区的前提的亚太区，大洋区、亚太区、日韩区，显示亚太区
    if (endpoint === MSCU && [APAC, OCEANIA, JPN_KOR].includes(maintenanceRegionCode as keyof typeof REGION_MAP)) {
        return REGION_MAP[APAC];
    }

    // 北美区前提下的日韩区，显示日韩区
    if (endpoint === MSNA && maintenanceRegionCode === JPN_KOR) {
        return REGION_MAP[maintenanceRegionCode as keyof typeof REGION_MAP];
    }

    // 默认都走endpoint
    return REGION_MAP[endpoint as keyof typeof REGION_MAP];
}

// 兼容基于单个SN的根据columbus 和 站点/梯控/地图，需要确保跳到目标区域设置对应的区域
export const getSwitchRegionArea = (columbus: string, maintenanceRegionCode: string) => {
    // 欧洲且亚太、日韩、大洋区，设置亚太
    if (columbus === MSCU && [APAC, OCEANIA, JPN_KOR].includes(maintenanceRegionCode as keyof typeof REGION_MAP)) {
        return APAC
    }

    //北美且日韩，设置日韩
    if (columbus === MSNA && maintenanceRegionCode === JPN_KOR) {
        return JPN_KOR;
    }

    // 除此之外的region，设置对应columbus对应的结果
    return BE_DC_REGION_MAP[columbus as keyof typeof BE_DC_REGION_MAP];
}

// 基于columbus 和 maintenanceRegionCode，设置目标区域
export const setRegionArea = (columbus: string, maintenanceRegionCode: string) => {
    localStorage.setItem('dc', BE_DC_REGION_MAP[columbus as keyof typeof BE_DC_REGION_MAP]);
    localStorage.setItem('maintenance_region', getSwitchRegionArea(columbus, maintenanceRegionCode));
}